import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Table,
  Tbody,
  Tr,
  Td,
  TableContainer,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Center,
  IconButton,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';

import { getAuth, AuthError } from '../../util/APIHelp';
import { Error } from '../error/Error';
import { getUsernameFromToken } from '../../util/IsAuth';
import { MyEventSource } from '../../util/MyEventSource';

const LogPage: React.FC = () => {
  const [logContent, setLogContent] = useState<string[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [isAutoscrollEnabled, setIsAutoscrollEnabled] = useState(true);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const bgColor = useColorModeValue('gray.100', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');

  const fetchLogContent = async () => {
    setPageLoading(true);
    setPageError(null);
    try {
      const response = await getAuth("/server/log");
      if (response.ok) {
        const logText = await response.text();
        const logLines = logText.split('\n').filter((line: string) => line.trim() !== '');
        setLogContent(logLines);
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        setPageError(`Failed to fetch log: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      if (error instanceof AuthError) {
        setPageError(error.message);
      } else if (error instanceof Error) {
        setPageError(String(error));
      } else {
        setPageError("An unexpected error occurred while fetching the log.");
      }
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    const username = getUsernameFromToken();
    const token = localStorage.getItem('token');
    
    if (!token) {
        setPageError("Authentication token not found.");
        return;
    }

    if(!username || username === "") {
        setPageError("Username not found.")
        return;
    }

    const eventSource = new MyEventSource(`/server/log/stream?username=${username}&token=${token}`);

    eventSource.onmessage = (event) => {
      const newLogLine = event.data;
      setLogContent(prevContent => {
        const updatedContent = [...prevContent, newLogLine];
        return updatedContent.slice(-1000);
      });
    };

    eventSource.onerror = (error) => {
      console.error("EventSource failed:", error);
      setPageError("Connection to log stream failed. See console for details.");
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, []);

  useEffect(() => {
    if (tableContainerRef.current && isAutoscrollEnabled) {
      const { scrollHeight, clientHeight } = tableContainerRef.current;
      tableContainerRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, [logContent, isAutoscrollEnabled]);

  if (pageError) {
    return <Error errorMessage={pageError} />;
  }

  return (
    <Box p={4} bg={bgColor}>
      <VStack align="stretch" spacing={6}>
        <HStack justifyContent="space-between">
          <Heading as="h1" size="xl" color={textColor}>
            Server Log
          </Heading>
          <HStack spacing={4}>
            <Button
              colorScheme={isAutoscrollEnabled ? 'teal' : 'gray'}
              onClick={() => setIsAutoscrollEnabled(!isAutoscrollEnabled)}
              size="sm"
            >
              {isAutoscrollEnabled ? 'Autoscroll On' : 'Autoscroll Off'}
            </Button>
            <IconButton
              aria-label="Refresh Log"
              icon={<FiRefreshCw />}
              onClick={fetchLogContent}
              isLoading={pageLoading}
              colorScheme="teal"
            />
          </HStack>
        </HStack>
        <Box p={4} bg={cardBg} borderRadius="md" boxShadow="sm">
          <TableContainer maxH="80vh" overflowY="auto" ref={tableContainerRef}>
            <Table variant="unstyled" size="sm">
              <Tbody>
                {logContent.length > 0 ? (
                  logContent.map((line, index) => (
                    <Tr key={index}>
                      <Td
                        border="none"
                        color={textColor}
                        fontFamily="monospace"
                        whiteSpace="pre-wrap"
                        wordBreak="break-word"
                      >
                        {line}
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={1} textAlign="center" color={textColor}>
                      No log content found.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
          {pageError && (
            <Alert status="error" variant="left-accent" mt={4}>
              <AlertIcon />
              <Text>{pageError}</Text>
            </Alert>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default LogPage;